/** Build a Set of upper-case alpha-2 codes from a countries list */
export const makeAlpha2Set = (countries = []) =>
  new Set(
    countries.map(c => (c && c.alpha2 ? String(c.alpha2).toUpperCase() : null)).filter(Boolean),
  );

/** Lookup country name from alpha-2, with fallback to the code itself */
export function countryNameFromAlpha2(alpha2, countries = []) {
  if (!alpha2) return '—';
  const code = String(alpha2).toUpperCase();
  return countries.find(c => c.alpha2 === code)?.name ?? code;
}

/** Title-case with smart handling for hyphens and apostrophes.
 *  - Capitalizes each word
 *  - Capitalizes parts after "-" and "'"
 *  - Keeps common short particles lower-case when not first/last (e.g., "van", "de", "of")
 */
export function toTitleCase(input = '', opts = {}) {
  if (!input) return '';
  const str = String(input).trim().replace(/\s+/g, ' ').toLowerCase();

  const defaultLower = [
    'and',
    'or',
    'the',
    'a',
    'an',
    'of',
    'in',
    'on',
    'at',
    'to',
    'for',
    'from',
    'by',
    'de',
    'da',
    'del',
    'della',
    'di',
    'du',
    'la',
    'le',
    'van',
    'von',
    'der',
    'den',
  ];
  const keepLower = new Set([...(opts.keepLower || []), ...defaultLower]);

  const cap = s => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

  // Capitalize hyphen/apostrophe separated segments in a single word
  const capSegments = word => {
    // Handle apostrophes first (e.g., O'Connor)
    const apost = word.split("'");
    const apostProcessed = apost.map(seg => {
      // Then handle hyphens inside each apostrophe segment
      return seg.split('-').map(cap).join('-');
    });
    return apostProcessed.map(cap).join("'");
  };

  const words = str.split(' ');
  const out = words.map((w, i) => {
    const isFirst = i === 0;
    const isLast = i === words.length - 1;

    // keep punctuation around word (rare in names, but safe)
    const m = w.match(/^([^A-Za-z0-9]*)([A-Za-z0-9\-']+)([^A-Za-z0-9]*)$/);
    if (!m) return w; // not a typical word
    const [, lead, core, trail] = m;

    // If whole core is a particle and not first/last -> keep lower-case
    if (keepLower.has(core) && !isFirst && !isLast) {
      return `${lead}${core}${trail}`;
    }

    return `${lead}${capSegments(core)}${trail}`;
  });

  return out.join(' ');
}

/** Convert any string to camelCase */
export function toCamelCase(input = '') {
  if (!input) return '';
  const s = String(input)
    // handle snake/camel/mixed
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_\-\s]+/g, ' ')
    .trim()
    .toLowerCase();
  const [first, ...rest] = s.split(' ');
  return [first || '', ...rest.map(w => w.charAt(0).toUpperCase() + w.slice(1))].join('');
}

/** Convert object keys to camelCase */
export function camelCaseObjectKeys(input, skipKeys = []) {
  if (Array.isArray(input)) {
    return input.map(v => camelCaseObjectKeys(v, skipKeys));
  }

  if (input && typeof input === 'object') {
    return Object.entries(input).reduce((acc, [k, v]) => {
      if (skipKeys.includes(k)) {
        acc[k] = v;
        return acc;
      }

      const outKey = toCamelCase(k);
      acc[outKey] = camelCaseObjectKeys(v, skipKeys);
      return acc;
    }, {});
  }

  return input;
}

/** Convert inner object keys to camelCase */
export const camelCaseInnerKeys = obj =>
  Object.fromEntries(
    Object.entries(obj || {}).map(([k, v]) => [
      k, // keep the top-level key as-is (wrapper code)
      v && typeof v === 'object' && !Array.isArray(v)
        ? camelCaseObjectKeys(v) // camelCase inner object keys only
        : v, // pass through primitives/arrays/null
    ]),
  );

/** Convert any string to snake_case */
export function toSnakeCase(input = '') {
  if (!input) return '';
  return String(input)
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2') // camel -> snake
    .replace(/[\s\-]+/g, '_') // spaces/dashes -> _
    .replace(/[^A-Za-z0-9_]/g, '_') // other punct -> _
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
}

/** Transform snake_case to Title Case, but keep 2-letter tokens that are valid alpha-2 codes UPPERCASE. */
export function snakeToTitleCase(input = '', alpha2Set /* Set of codes */) {
  if (!input) return '';
  const tokens = String(input).split('_');
  return tokens
    .map(tok => {
      if (tok.length === 2 && alpha2Set && alpha2Set.has(tok.toUpperCase())) {
        return tok.toUpperCase();
      }
      return toTitleCase(tok);
    })
    .join(' ');
}
